
import React, { useMemo, useState, useEffect } from 'react';
import { Reservation, Table, Zone, CategoryDefinition, ArenaConfig } from '../types';
import { Clock, Users, Plus, X, Check, AlertTriangle, LayoutGrid, Target, Pencil, Trash2, Inbox, Repeat, BellOff, CheckCircle2, Package, MessageSquare } from 'lucide-react';
import { ReservationModal } from './ReservationModal';

interface AdminReservationListProps {
  reservations: Reservation[];
  tables: Table[];
  zones: Zone[];
  categories: CategoryDefinition[];
  onMakeReservation: (res: Reservation) => void;
  onUpdateReservation?: (res: Reservation) => void;
  onCancelReservation?: (id: string) => void;
  onCancelSeries?: (seriesId: string) => void;
  config: ArenaConfig;
  scrollToTableId?: string | null;
  onScrollComplete?: () => void;
  acknowledgedIds?: Set<string>;
  onAcknowledge?: (id: string) => void;
}

type GroupingMode = 'ZONE' | 'TABLE';
type DateFilter = 'TODAY' | 'WEEK' | 'MONTH' | 'ALL';

export const AdminReservationList: React.FC<AdminReservationListProps> = ({
  reservations,
  tables,
  zones,
  categories,
  onMakeReservation,
  onUpdateReservation,
  onCancelReservation,
  onCancelSeries,
  config,
  scrollToTableId,
  onScrollComplete,
  acknowledgedIds,
  onAcknowledge,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [groupingMode, setGroupingMode] = useState<GroupingMode>('TABLE');
  const [dateFilter, setDateFilter] = useState<DateFilter>('ALL');

  const isDarkMode = config.theme?.darkMode;
  const isSimple = config.theme?.mode === 'SIMPLE';

  useEffect(() => {
    if (scrollToTableId) {
      setGroupingMode('TABLE');
      const timer = setTimeout(() => {
        const element = document.getElementById(`table-group-${scrollToTableId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-4', 'ring-emerald-500/20');
          setTimeout(() => element.classList.remove('ring-4', 'ring-emerald-500/20'), 2000);
        }
        onScrollComplete?.();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollToTableId]);

  const isWithinFilter = (dateStr: string, filter: DateFilter) => {
    if (filter === 'ALL') return true;
    const d = new Date(dateStr); d.setHours(0,0,0,0);
    const now = new Date(); now.setHours(0,0,0,0);
    if (filter === 'TODAY') return dateStr === now.toISOString().split('T')[0];
    if (filter === 'WEEK') {
      const start = new Date(now); start.setDate(now.getDate() - (now.getDay() === 0 ? 6 : now.getDay() - 1));
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return d >= start && d <= end;
    }
    if (filter === 'MONTH') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    return true;
  };

  const filteredReservations = useMemo(() => reservations.filter(r => isWithinFilter(r.date, dateFilter)), [reservations, dateFilter]);

  // Notification lists — not filtered by date, always show until acknowledged
  const newConfirmedReservations = useMemo(() =>
    reservations.filter(r => r.status === 'CONFIRMED' && !acknowledgedIds?.has(r.id))
      .sort((a, b) => new Date(b.date + 'T' + b.time).getTime() - new Date(a.date + 'T' + a.time).getTime()),
    [reservations, acknowledgedIds]);

  const guestCancellations = useMemo(() =>
    reservations.filter(r => r.status === 'CANCELLED_BY_GUEST' && !acknowledgedIds?.has(r.id))
      .sort((a, b) => new Date(a.date + 'T' + a.time).getTime() - new Date(b.date + 'T' + b.time).getTime()),
    [reservations, acknowledgedIds]);

  // Pending notifications must NOT be date-filtered — admin must always see all open requests
  const pendingReservations = useMemo(() => reservations.filter(r => r.status === 'PENDING').sort((a,b) => new Date(a.date+'T'+a.time).getTime() - new Date(b.date+'T'+b.time).getTime()), [reservations]);
  const confirmedReservations = useMemo(() => filteredReservations.filter(r => r.status === 'CONFIRMED' || !r.status), [filteredReservations]);

  const pendingSeriesGroups = useMemo(() => {
    const groups: Record<string, Reservation[]> = {};
    pendingReservations.forEach(r => {
      if (r.seriesId) {
        if (!groups[r.seriesId]) groups[r.seriesId] = [];
        groups[r.seriesId].push(r);
      }
    });
    return groups;
  }, [pendingReservations]);

  const singlePendingReservations = useMemo(() => pendingReservations.filter(r => !r.seriesId), [pendingReservations]);

  const getTableRect = (table: Table) => {
    const width = table.width || 60;
    const height = table.height || 60;
    return { x: table.x, y: table.y, width, height };
  };

  const getZoneForTable = (table: Table) => {
    const tableRect = getTableRect(table);
    let maxArea = 0; let foundZone: Zone | null = null;
    zones.forEach(zone => {
      const xOverlap = Math.max(0, Math.min(tableRect.x + tableRect.width, zone.x + zone.width) - Math.max(tableRect.x, zone.x));
      const yOverlap = Math.max(0, Math.min(tableRect.y + tableRect.height, zone.y + zone.height) - Math.max(tableRect.y, zone.y));
      const area = xOverlap * yOverlap;
      if (area > maxArea) { maxArea = area; foundZone = zone; }
    });
    return foundZone;
  };

  const getEndTime = (time: string, duration: number) => {
    const [h, m] = time.split(':').map(Number);
    const date = new Date(); date.setHours(h, m + duration, 0, 0);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const groupedReservations = useMemo(() => {
    const groups: Record<string, { id: string; title: string; subtitle?: string; items: (Reservation & { tableName: string; zoneName: string })[] }> = {};
    
    tables.forEach(table => {
      const zone = getZoneForTable(table);
      const zoneName = zone ? (zone as any).name : 'Allgemein';
      const groupId = groupingMode === 'ZONE' ? (zone ? (zone as any).id : 'unassigned') : table.id;
      
      if (!groups[groupId]) {
        groups[groupId] = { 
          id: groupId, 
          title: groupingMode === 'ZONE' ? zoneName : table.name, 
          subtitle: groupingMode === 'TABLE' ? zoneName : undefined, 
          items: [] 
        };
      }
    });

    confirmedReservations.forEach(res => {
      const table = tables.find(t => t.id === res.tableId); 
      if (!table) return;
      const zone = getZoneForTable(table); 
      const zoneName = zone ? (zone as any).name : 'Allgemein';
      const groupId = groupingMode === 'ZONE' ? (zone ? (zone as any).id : 'unassigned') : table.id;
      
      if (groups[groupId]) {
        groups[groupId].items.push({ ...res, tableName: table.name, zoneName });
      }
    });

    return Object.values(groups)
      .map(g => ({ ...g, items: g.items.sort((a,b) => new Date(`${a.date}T${a.time}`).getTime() - new Date(`${b.date}T${b.time}`).getTime()) }))
      .sort((a,b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
  }, [confirmedReservations, tables, zones, groupingMode]);

  const handleCancel = (resId: string) => { if (window.confirm('Möchten Sie diese Reservierung wirklich löschen?')) onCancelReservation?.(resId); };

  const handleApproveSeries = (seriesId: string) => {
    const resInSeries = reservations.filter(r => r.seriesId === seriesId) as Reservation[];
    resInSeries.forEach(r => {
      onUpdateReservation?.({ ...r, status: 'CONFIRMED' });
      onAcknowledge?.(r.id);
    });
  };

  const isTableAvailable = (tableId: string, date: string, time: string, duration: number, excludeResId?: string) => {
    const reqStart = new Date(`${date}T${time}`).getTime();
    const reqEnd = reqStart + duration * 60000;
    return !reservations.some(res => {
      if (res.id === (excludeResId || editingReservation?.id)) return false;
      if (res.tableId !== tableId || res.date !== date || res.status === 'PENDING' || res.status === 'CANCELLED_BY_GUEST') return false;
      const resStart = new Date(`${res.date}T${res.time}`).getTime();
      const resEnd = resStart + res.durationMinutes * 60000;
      return reqStart < resEnd && reqEnd > resStart;
    });
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8 animate-fade-in relative">

      {/* Neue bestätigte Buchungen */}
      {newConfirmedReservations.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl overflow-hidden shadow-sm mb-8 animate-fade-in ring-2 ring-green-500/20">
          <div className="px-6 py-3 bg-green-100 border-b border-green-200 flex items-center justify-between">
            <h3 className="font-bold text-green-900 flex items-center gap-2">
              <CheckCircle2 size={18} className="text-green-600" /> Neue bestätigte Buchungen
            </h3>
            <div className="flex items-center gap-3">
              <span className="bg-green-200 text-green-800 text-[10px] font-black px-2 py-0.5 rounded-full uppercase">{newConfirmedReservations.length}</span>
              <button onClick={() => newConfirmedReservations.forEach(r => onAcknowledge?.(r.id))} className="text-xs font-bold text-green-700 hover:text-green-900 underline underline-offset-2">Alle als gelesen</button>
            </div>
          </div>
          <div className="divide-y divide-green-100">
            {newConfirmedReservations.map(res => {
              const table = tables.find(t => t.id === res.tableId);
              return (
                <div key={res.id} className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white/40 hover:bg-green-50/50 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-800">{res.customerName}</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-black uppercase rounded-full">Bestätigt</span>
                      {res.seriesId && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase rounded-full flex items-center gap-1"><Repeat size={8} /> Serie</span>}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="font-medium">{new Date(res.date).toLocaleDateString('de-DE')} • {res.time} Uhr</span>
                      <span className="flex items-center gap-1 text-slate-500 font-bold"><Target size={12} /> {table?.name || 'Unbekannt'}</span>
                      <span className="flex items-center gap-1 text-slate-500"><Users size={12} /> {res.partySize} Pers.</span>
                      {res.packageName && <span className="flex items-center gap-1 text-emerald-700 font-bold bg-emerald-50 px-1.5 py-0.5 rounded"><Package size={11} /> {res.packageName}</span>}
                      {res.notes && <span className="flex items-center gap-1 text-slate-500 italic"><MessageSquare size={11} /> "{res.notes}"</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => onAcknowledge?.(res.id)}
                    className="px-4 py-2 bg-white border border-green-200 text-green-700 font-bold rounded shadow-sm hover:bg-green-50 flex items-center gap-2 text-xs uppercase tracking-widest whitespace-nowrap"
                  >
                    <Check size={14} /> Als gelesen
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stornierungen durch Gäste */}
      {guestCancellations.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl overflow-hidden shadow-sm mb-8 animate-fade-in ring-2 ring-red-500/20">
              <div className="px-6 py-3 bg-red-100 border-b border-red-200 flex items-center justify-between">
                  <h3 className="font-bold text-red-900 flex items-center gap-2">
                      <BellOff size={18} className="animate-bounce" /> Neue Stornierungen durch Gäste
                  </h3>
                  <span className="bg-red-200 text-red-800 text-[10px] font-black px-2 py-0.5 rounded-full uppercase">{guestCancellations.length}</span>
              </div>
              <div className="divide-y divide-red-100">
                  {guestCancellations.map(res => {
                      const table = tables.find(t => t.id === res.tableId);
                      return (
                          <div key={res.id} className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white/40 hover:bg-red-100/30 transition-colors">
                              <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                      <span className="font-bold text-slate-800 line-through opacity-60">{res.customerName}</span>
                                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-black uppercase rounded-full">Storniert</span>
                                  </div>
                                  <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                                      <span className="font-medium">{new Date(res.date).toLocaleDateString('de-DE')} • {res.time} Uhr</span>
                                      <span className="flex items-center gap-1 text-slate-500 font-bold"><Target size={12}/> {table?.name || 'Unbekannt'}</span>
                                      {res.notes && <span className="text-[10px] italic text-slate-400 truncate max-w-xs">"{res.notes}"</span>}
                                  </div>
                              </div>
                              <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => onAcknowledge?.(res.id)}
                                    className="px-4 py-2 bg-white border border-red-200 text-red-600 font-bold rounded shadow-sm hover:bg-red-50 flex items-center gap-2 text-xs uppercase tracking-widest"
                                  >
                                      <Check size={14} /> Gelesen & Entfernen
                                  </button>
                              </div>
                          </div>
                      );
                  })}
              </div>
          </div>
      )}

      {/* Series Pending Section */}
      {Object.keys(pendingSeriesGroups).length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl overflow-hidden shadow-sm mb-8 animate-fade-in">
              <div className="px-6 py-3 bg-emerald-100 border-b border-emerald-200 flex items-center justify-between">
                  <h3 className="font-bold text-emerald-900 flex items-center gap-2">
                      <Repeat size={18} /> Anfragen für Serien-Abos
                  </h3>
              </div>
              <div className="divide-y divide-emerald-100">
                  {(Object.entries(pendingSeriesGroups) as [string, Reservation[]][]).map(([seriesId, resArray]) => {
                      const first = resArray[0];
                      const table = tables.find(t => t.id === first.tableId);
                      const hasAnyConflict = resArray.some(r => !isTableAvailable(r.tableId, r.date, r.time, r.durationMinutes, r.id));
                      return (
                          <div key={seriesId} className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-emerald-50/50 hover:bg-emerald-100/50 transition-colors">
                              <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                      <span className="font-bold text-slate-800">{first.customerName}</span>
                                      <span className="px-2 py-0.5 bg-white border border-emerald-200 text-emerald-800 text-[10px] font-black uppercase rounded-full">
                                          {resArray.length} Termine
                                      </span>
                                  </div>
                                  <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                                      <span>Wöchentlich ab {new Date(first.date).toLocaleDateString('de-DE')}</span>
                                      <span className="font-medium bg-emerald-100/50 px-1 rounded text-emerald-900">{first.time} Uhr</span>
                                      <span className="flex items-center gap-1 text-slate-500"><Target size={12}/> {table?.name || 'Unbekannt'}</span>
                                      {first.packageName && <span className="flex items-center gap-1 text-emerald-700 font-bold bg-emerald-50 px-1.5 py-0.5 rounded"><Package size={11} /> {first.packageName}</span>}
                                      {first.notes && <span className="flex items-center gap-1 text-slate-500 italic"><MessageSquare size={11} /> "{first.notes}"</span>}
                                  </div>
                              </div>
                              <div className="flex items-center gap-3">
                                  {hasAnyConflict && (
                                      <div className="text-red-500 flex items-center gap-1 text-[10px] font-bold uppercase">
                                          <AlertTriangle size={14} /> Konflikt in Serie
                                      </div>
                                  )}
                                  <div className="flex gap-2">
                                      <button onClick={() => { if(window.confirm("Ganze Serie löschen?")) { onCancelSeries?.(seriesId); resArray.forEach(r => onAcknowledge?.(r.id)); } }} className="p-2 text-red-600 bg-white border border-red-200 rounded hover:bg-red-50"><Trash2 size={18} /></button>
                                      <button onClick={() => handleApproveSeries(seriesId)} className="px-4 py-2 bg-emerald-600 text-white font-bold rounded shadow-sm hover:bg-emerald-700 flex items-center gap-2">
                                          Serie genehmigen <Check size={18} />
                                      </button>
                                  </div>
                              </div>
                          </div>
                      );
                  })}
              </div>
          </div>
      )}

      {/* Single Pending Section */}
      {singlePendingReservations.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden shadow-sm mb-8 animate-fade-in">
              <div className="px-6 py-3 bg-amber-100 border-b border-amber-200 flex items-center justify-between"><h3 className="font-bold text-amber-900 flex items-center gap-2"><Clock size={18} /> Offene Anfragen (Einzel)</h3></div>
              <div className="divide-y divide-amber-100">
                  {singlePendingReservations.map(req => {
                      const endTime = getEndTime(req.time, req.durationMinutes);
                      const hasConflict = req.tableId ? !isTableAvailable(req.tableId, req.date, req.time, req.durationMinutes, req.id) : false;
                      return (
                          <div key={req.id} className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-amber-50/50 hover:bg-amber-100/50 transition-colors">
                              <div className="flex-1">
                                  <div className="flex items-center gap-2"><span className="font-bold text-slate-800">{req.customerName}</span><span className="text-xs bg-white border border-amber-200 px-2 py-0.5 rounded text-amber-800"><Users size={10} /> {req.partySize}</span></div>
                                  <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1"><span>{new Date(req.date).toLocaleDateString('de-DE')}</span><span className="font-medium bg-amber-100/50 px-1 rounded text-amber-900">{req.time} - {endTime} Uhr</span>
                                  {req.packageName && <span className="flex items-center gap-1 text-emerald-700 font-bold bg-emerald-50 px-1.5 py-0.5 rounded"><Package size={11} /> {req.packageName}</span>}
                                  {req.notes && <span className="flex items-center gap-1 text-slate-500 italic"><MessageSquare size={11} /> "{req.notes}"</span>}
                                  </div>
                              </div>
                              <div className="flex items-center gap-3 w-full md:w-auto">
                                  <div className="flex flex-col relative">
                                      <label className="text-[10px] uppercase font-bold text-slate-400 mb-0.5 flex justify-between">Zuweisung {hasConflict && <span className="text-red-500 flex items-center gap-1"><AlertTriangle size={10}/> Konflikt</span>}</label>
                                      <select className={`text-sm border rounded px-2 py-1.5 outline-none w-52 ${hasConflict ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-white text-slate-800'}`} value={req.tableId || ''} onChange={(e) => onUpdateReservation?.({ ...req, tableId: e.target.value })}>
                                          <option value="">-- Kein Platz --</option>
                                          {tables.filter(t => t.capacity >= req.partySize).sort((a,b) => a.name.localeCompare(b.name, undefined, {numeric: true})).map(t => (
                                              <option key={t.id} value={t.id} disabled={!isTableAvailable(t.id, req.date, req.time, req.durationMinutes, req.id)}>{t.name} ({t.capacity} Pers.)</option>
                                          ))}
                                      </select>
                                  </div>
                                  <div className="flex gap-2">
                                      <button onClick={() => { handleCancel(req.id); onAcknowledge?.(req.id); }} className="p-2 text-red-600 bg-white border border-red-200 rounded hover:bg-red-50"><X size={18} /></button>
                                      <button onClick={() => { onUpdateReservation?.({ ...req, status: 'CONFIRMED' }); onAcknowledge?.(req.id); }} disabled={!req.tableId || hasConflict} className={`px-4 py-2 text-white font-bold rounded shadow-sm ${!req.tableId || hasConflict ? 'bg-slate-300' : 'bg-green-600 hover:bg-green-700'}`}><Check size={18} /> Bestätigen</button>
                                  </div>
                              </div>
                          </div>
                      );
                  })}
              </div>
          </div>
      )}

      {/* Header section with title and global controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
        <div>
           <h2 className="text-xl md:text-2xl font-bold text-slate-800">Reservierungsübersicht</h2>
           <div className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Anzeige für: <span className={`font-bold ${isDarkMode ? 'theme-text-primary' : 'text-slate-900'}`}>{dateFilter === 'ALL' ? 'Gesamter Zeitraum' : dateFilter === 'TODAY' ? 'Heute' : dateFilter === 'WEEK' ? 'Diese Woche' : 'Diesen Monat'}</span></div>
           <div className="flex bg-white border border-slate-200 p-1 rounded-xl shadow-sm inline-flex">
              {['TODAY', 'WEEK', 'MONTH', 'ALL'].map(f => (
                <button key={f} onClick={() => setDateFilter(f as DateFilter)} className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${dateFilter === f ? 'theme-bg-secondary text-white shadow-md' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}>{f === 'TODAY' ? 'Heute' : f === 'WEEK' ? 'Woche' : f === 'MONTH' ? 'Monat' : 'Gesamt'}</button>
              ))}
           </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
            <button onClick={() => setGroupingMode('TABLE')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${groupingMode === 'TABLE' ? 'bg-white theme-text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}><Target size={14} /> Je Platz</button>
            <button onClick={() => setGroupingMode('ZONE')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${groupingMode === 'ZONE' ? 'bg-white theme-text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}><LayoutGrid size={14} /> Je Halle</button>
          </div>
          <button onClick={() => { setEditingReservation(null); setIsModalOpen(true); }} className="flex items-center justify-center gap-2 theme-bg-secondary text-white px-4 py-2 rounded-lg font-medium hover:opacity-90 shadow transition-all whitespace-nowrap"><Plus size={18} /> neue Reservierung</button>
        </div>
      </div>

      {groupedReservations.map((group, idx) => (
          <div key={idx} id={`table-group-${group.id}`} className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6 transition-all duration-500 scroll-mt-20 ${isDarkMode ? 'border-gray-700' : ''}`}>
            <div className={`px-6 py-3 border-b flex items-center justify-between ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-2">
                <Target size={16} className={isDarkMode ? 'theme-text-primary' : 'text-slate-400'} />
                <div>
                  <h3 className={`font-bold ${isDarkMode ? 'theme-text-primary' : 'text-slate-700'}`}>{group.title}</h3>
                  {group.subtitle && <span className={`text-[10px] font-bold uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>{group.subtitle}</span>}
                </div>
              </div>
              <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${isDarkMode ? 'bg-gray-700 text-slate-300' : 'bg-slate-200/50 text-slate-500'}`}>
                {group.items.length} {group.items.length === 1 ? 'Buchung' : 'Buchungen'}
              </span>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className={`text-[10px] uppercase tracking-widest border-b ${isDarkMode ? 'bg-gray-900 border-gray-700 text-slate-500' : 'bg-white border-slate-100 text-slate-500'}`}>
                  <tr>
                    <th className="px-6 py-4 font-bold">Datum & Zeit</th>
                    <th className="px-6 py-4 font-bold">Gast / Verein</th>
                    <th className="px-6 py-4 font-bold">Ort</th>
                    <th className="px-6 py-4 font-bold text-center">Team</th>
                    <th className="px-6 py-4 font-bold text-right">Dauer</th>
                    <th className="px-6 py-4 font-bold text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-slate-50'}`}>
                  {group.items.length > 0 ? (
                    group.items.map((res) => (
                      <tr key={res.id} className={`transition-colors group ${isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-slate-50/80'}`}>
                        <td className="px-6 py-4">
                          <div>
                            <div className={`font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>{res.time} - {getEndTime(res.time, res.durationMinutes)}</div>
                            <div className={`text-[10px] font-bold uppercase ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{new Date(res.date).toLocaleDateString('de-DE')}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{res.customerName}</div>
                          {res.seriesId && (
                              <div className="flex items-center gap-1 text-[9px] text-emerald-600 font-bold uppercase mt-0.5">
                                  <Repeat size={10} /> Abo-Serie
                              </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-black uppercase border ${groupingMode === 'ZONE' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
                            {groupingMode === 'ZONE' ? res.tableName : res.zoneName}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className={`flex items-center justify-center gap-1.5 font-bold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                            <Users size={14} />
                            <span>{res.partySize}</span>
                          </div>
                        </td>
                        <td className={`px-6 py-4 text-right font-medium ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{res.durationMinutes}m</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setEditingReservation(res); setIsModalOpen(true); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Bearbeiten"><Pencil size={16} /></button>
                            <button onClick={() => handleCancel(res.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Löschen"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center">
                        <div className="flex flex-col items-center justify-center text-slate-300">
                          <Inbox size={24} className="mb-2 opacity-20" />
                          <span className="text-[10px] font-black uppercase tracking-widest italic">Keine Buchungen vorhanden</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
      ))}

      <ReservationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={(res) => { if (editingReservation) onUpdateReservation?.(res); else onMakeReservation(res); }} 
        editingReservation={editingReservation} 
        tables={tables} categories={categories} reservations={reservations} 
        config={config}
      />
    </div>
  );
};
